/**
 * TenantDetailDrawer Component
 * Full-height slide-out drawer for admin tenant management with 5 tabs
 */

import { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Database,
  DollarSign,
  ToggleLeft,
  Gauge,
  Shield,
  Loader2,
  Check,
  AlertTriangle,
  RefreshCw,
  Plus,
  Trash2,
  FileText,
} from 'lucide-react';
import { useTenantDetails, useTenantUsage } from '../../hooks/useTenant';
import { useTenantConfig, useUpdateTenantConfig } from '../../hooks/useTenantConfig';
import { useTenantLLMCosts } from '../../hooks/useLLMCosts';
import Toggle from '../common/Toggle';
import BlobStorageTab from './BlobStorageTab';
import AuditTrailModal from './AuditTrailModal';

// Tab configuration
const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'storage', label: 'Storage', icon: Database },
  { id: 'llm-costs', label: 'LLM Costs', icon: DollarSign },
  { id: 'features', label: 'Feature Flags', icon: ToggleLeft },
  { id: 'limits', label: 'Limits & Alerts', icon: Gauge },
  { id: 'access', label: 'Access Control', icon: Shield },
];

// Feature flag definitions
const FEATURE_FLAGS = [
  { key: 'ensemble_pricing', label: 'Ensemble Pricing', description: 'Enable AI ensemble pricing models' },
  { key: 'webhooks', label: 'Webhooks', description: 'Allow webhook integrations' },
  { key: 'competitor_monitoring', label: 'Competitor Monitoring', description: 'Real-time competitor price tracking' },
  { key: 'demand_forecasting', label: 'Demand Forecasting', description: 'AI-powered demand predictions' },
  { key: 'custom_pricing_rules', label: 'Custom Pricing Rules', description: 'Define custom pricing logic' },
  { key: 'white_label', label: 'White Label', description: 'Remove ServiceHive branding' },
];

// Overview Tab Component
function OverviewTab({ tenantId }) {
  const { data: tenant, isLoading: tenantLoading } = useTenantDetails(tenantId);
  const { data: usage, isLoading: usageLoading } = useTenantUsage(tenantId);

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tenantInfo = tenant?.tenant || tenant;
  const usageData = usage?.usage || usage;

  return (
    <div className="space-y-6">
      {/* Tenant Info Card */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Tenant Information</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-400">Name</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{tenantInfo?.name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs text-gray-400">Slug</label>
            <p className="text-sm font-mono text-gray-900 dark:text-white">{tenantInfo?.slug || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs text-gray-400">Status</label>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
              tenantInfo?.status === 'active'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
            }`}>
              {tenantInfo?.status || 'unknown'}
            </span>
          </div>
          <div>
            <label className="text-xs text-gray-400">Plan</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{tenantInfo?.plan || 'N/A'}</p>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-400">Created</label>
            <p className="text-sm text-gray-900 dark:text-white">
              {tenantInfo?.created_at ? new Date(tenantInfo.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }) : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {usageLoading ? '-' : (usageData?.users_count || 0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Users</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {usageLoading ? '-' : (usageData?.active_trips || 0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Active Trips</p>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {usageLoading ? '-' : (usageData?.token_balance?.toLocaleString() || 0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Token Balance</p>
        </div>
      </div>
    </div>
  );
}

// Helper: Get date N days ago in YYYY-MM-DD format
function getDateNDaysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

// Helper: Get today in YYYY-MM-DD format
function getToday() {
  return new Date().toISOString().split('T')[0];
}

// LLM Costs Tab Component
function LLMCostsTab({ tenantId, onOpenAuditTrail }) {
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState(getDateNDaysAgo(30));
  const [endDate, setEndDate] = useState(getToday());

  const { data: costsData, isLoading, refetch } = useTenantLLMCosts(tenantId, period, startDate, endDate);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const costs = costsData?.data || costsData || {};
  // Backend returns flat structure: total_cost_usd, total_tokens, etc. (not under 'summary')
  const byModel = costs.by_model || [];
  const byOperation = costs.by_operation || [];
  const totalCost = costs.total_cost_usd || 0;
  const totalTokens = costs.total_tokens || 0;
  const avgLatencyMs = costs.avg_latency_ms || 0;
  const totalCalls = costs.total_calls || 0;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setPeriod('daily')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              period === 'daily'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Daily
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              period === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Monthly
          </button>
        </div>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
        <span className="text-gray-400">to</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
        <button
          onClick={() => refetch()}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">Total Cost</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            ${totalCost.toFixed(4)}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">Total Tokens</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {totalTokens.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">Avg Latency</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {avgLatencyMs.toFixed(0)}ms
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">Total Calls</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {totalCalls.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Cost by Model Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Cost by Model</h4>
        {byModel.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm text-gray-500 dark:text-gray-400">
            No model data available
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">Model</th>
                  <th className="text-right px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">Tokens</th>
                  <th className="text-right px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">Cost</th>
                  <th className="text-right px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {byModel.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-gray-900 dark:text-white font-mono text-xs">{row.model}</td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">
                      {(row.tokens || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-white font-medium">
                      ${(row.cost || 0).toFixed(4)}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">
                      {totalCost > 0 ? ((row.cost / totalCost) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cost by Operation Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Cost by Operation</h4>
        {byOperation.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm text-gray-500 dark:text-gray-400">
            No operation data available
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">Operation</th>
                  <th className="text-right px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">Calls</th>
                  <th className="text-right px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {byOperation.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{row.operation || row.operation_type || '-'}</td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">
                      {(row.calls || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-white font-medium">
                      ${(row.cost || row.cost_usd || 0).toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Audit Trail Button */}
      <button
        onClick={onOpenAuditTrail}
        className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        <FileText className="w-4 h-4" />
        View Audit Trail
      </button>
    </div>
  );
}

// Feature Flags Tab Component
function FeatureFlagsTab({ tenantId }) {
  const { data: configData, isLoading } = useTenantConfig(tenantId);
  const updateConfig = useUpdateTenantConfig();
  const [localFlags, setLocalFlags] = useState({});

  useEffect(() => {
    if (configData?.config?.feature_flags) {
      setLocalFlags(configData.config.feature_flags);
    }
  }, [configData]);

  const handleToggle = async (flagKey, newValue) => {
    const newFlags = { ...localFlags, [flagKey]: newValue };
    setLocalFlags(newFlags);

    try {
      await updateConfig.mutateAsync({
        tenantId,
        updates: { feature_flags: newFlags },
      });
    } catch (error) {
      // Revert on error
      setLocalFlags(configData?.config?.feature_flags || {});
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {FEATURE_FLAGS.map((flag) => (
        <div
          key={flag.key}
          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
        >
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{flag.label}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{flag.description}</p>
          </div>
          <Toggle
            enabled={localFlags[flag.key] || false}
            onChange={(value) => handleToggle(flag.key, value)}
          />
        </div>
      ))}

      {updateConfig.isPending && (
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}

// Helper: Get first day of current month
function getFirstOfMonth() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
}

// Progress bar component for usage visualization
function UsageProgressBar({ label, current, limit, unit, warningThreshold = 80 }) {
  const percentage = limit > 0 ? Math.min(100, (current / limit) * 100) : 0;
  const isWarning = percentage >= warningThreshold;
  const isExceeded = percentage >= 100;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className={`font-medium ${isExceeded ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-gray-700 dark:text-gray-300'}`}>
          {current.toLocaleString()} / {limit > 0 ? limit.toLocaleString() : '∞'} {unit}
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isExceeded ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-blue-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-right text-xs text-gray-400">
        {percentage.toFixed(1)}%
      </div>
    </div>
  );
}

// Limits & Alerts Tab Component
function LimitsTab({ tenantId }) {
  const { data: configData, isLoading } = useTenantConfig(tenantId);
  const updateConfig = useUpdateTenantConfig();

  // Fetch current month's usage for progress bars
  const startOfMonth = getFirstOfMonth();
  const today = getToday();
  const { data: costsData } = useTenantLLMCosts(tenantId, 'monthly', startOfMonth, today);

  // Backend returns flat structure: total_cost_usd, total_tokens (not under 'summary')
  const costs = costsData?.data || costsData || {};
  const currentUsage = {
    cost: costs.total_cost_usd || 0,
    tokens: costs.total_tokens || 0,
  };

  // State split to match backend structure: limits, access, alerts are separate sections
  const [limits, setLimits] = useState({
    monthly_cost_cap_usd: null,
    monthly_token_cap: null,
    requests_per_minute: 100,
    enforcement: 'hard',  // Backend uses 'enforcement', not 'enforcement_mode'
  });
  const [access, setAccess] = useState({
    max_concurrent_requests: 50,  // This is in 'access', not 'limits'
  });
  const [alerts, setAlerts] = useState({
    cost_threshold_percent: 80,  // This is in 'alerts', not 'limits'
  });

  useEffect(() => {
    if (configData?.config) {
      const cfg = configData.config;
      setLimits({
        monthly_cost_cap_usd: cfg.limits?.monthly_cost_cap_usd ?? null,
        monthly_token_cap: cfg.limits?.monthly_token_cap ?? null,
        requests_per_minute: cfg.limits?.requests_per_minute ?? 100,
        enforcement: cfg.limits?.enforcement ?? 'hard',
      });
      setAccess({
        max_concurrent_requests: cfg.access?.max_concurrent_requests ?? 50,
      });
      setAlerts({
        cost_threshold_percent: cfg.alerts?.cost_threshold_percent ?? 80,
      });
    }
  }, [configData]);

  const handleSave = async () => {
    // Save all three sections in one update
    await updateConfig.mutateAsync({
      tenantId,
      updates: { limits, access, alerts },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Usage Progress Bars */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Month Usage</h4>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-4">
          <UsageProgressBar
            label="Cost Usage"
            current={currentUsage.cost}
            limit={limits.monthly_cost_cap_usd || 0}
            unit="USD"
            warningThreshold={alerts.cost_threshold_percent}
          />
          <UsageProgressBar
            label="Token Usage"
            current={currentUsage.tokens}
            limit={limits.monthly_token_cap || 0}
            unit="tokens"
            warningThreshold={alerts.cost_threshold_percent}
          />
        </div>
      </div>

      {/* Cost Limits */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Cost Limits</h4>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Monthly Cost Cap (USD)
          </label>
          <input
            type="number"
            value={limits.monthly_cost_cap_usd}
            onChange={(e) => setLimits(l => ({ ...l, monthly_cost_cap_usd: parseFloat(e.target.value) || 0 }))}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            min="0"
            step="10"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Monthly Token Cap
          </label>
          <input
            type="number"
            value={limits.monthly_token_cap}
            onChange={(e) => setLimits(l => ({ ...l, monthly_token_cap: parseInt(e.target.value) || 0 }))}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            min="0"
            step="10000"
          />
        </div>
      </div>

      {/* Rate Limits */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Rate Limits</h4>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Requests per Minute
          </label>
          <input
            type="number"
            value={limits.requests_per_minute}
            onChange={(e) => setLimits(l => ({ ...l, requests_per_minute: parseInt(e.target.value) || 0 }))}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            min="1"
            max="1000"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Max Concurrent Requests
          </label>
          <input
            type="number"
            value={access.max_concurrent_requests}
            onChange={(e) => setAccess(a => ({ ...a, max_concurrent_requests: parseInt(e.target.value) || 1 }))}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            min="1"
            max="100"
          />
        </div>
      </div>

      {/* Enforcement */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enforcement</h4>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Enforcement Mode
          </label>
          <select
            value={limits.enforcement}
            onChange={(e) => setLimits(l => ({ ...l, enforcement: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="alert_only">Alert Only</option>
            <option value="soft">Soft (Warning + Allow)</option>
            <option value="hard">Hard (Block Requests)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            Alert Threshold (%)
          </label>
          <input
            type="number"
            value={alerts.cost_threshold_percent}
            onChange={(e) => setAlerts(a => ({ ...a, cost_threshold_percent: parseInt(e.target.value) || 80 }))}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            min="1"
            max="100"
          />
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={updateConfig.isPending}
        className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        {updateConfig.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Check className="w-4 h-4" />
            Save Limits
          </>
        )}
      </button>
    </div>
  );
}

// Access Control Tab Component
function AccessTab({ tenantId }) {
  const { data: configData, isLoading } = useTenantConfig(tenantId);
  const updateConfig = useUpdateTenantConfig();

  const [access, setAccess] = useState({
    max_concurrent_requests: 10,
    ip_allowlist: [],
  });
  const [newIp, setNewIp] = useState('');

  useEffect(() => {
    if (configData?.config?.access) {
      setAccess({
        max_concurrent_requests: configData.config.access.max_concurrent_requests || 10,
        ip_allowlist: configData.config.access.ip_allowlist || [],
      });
    }
  }, [configData]);

  const handleAddIp = () => {
    if (!newIp.trim()) return;
    // Basic IP validation
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipPattern.test(newIp.trim())) {
      return;
    }
    setAccess(a => ({
      ...a,
      ip_allowlist: [...a.ip_allowlist, newIp.trim()],
    }));
    setNewIp('');
  };

  const handleRemoveIp = (ip) => {
    setAccess(a => ({
      ...a,
      ip_allowlist: a.ip_allowlist.filter(i => i !== ip),
    }));
  };

  const handleSave = async () => {
    await updateConfig.mutateAsync({
      tenantId,
      updates: { access },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Concurrent Requests */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Max Concurrent Requests
        </label>
        <input
          type="number"
          value={access.max_concurrent_requests}
          onChange={(e) => setAccess(a => ({ ...a, max_concurrent_requests: parseInt(e.target.value) || 0 }))}
          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          min="1"
          max="100"
        />
      </div>

      {/* IP Allowlist */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          IP Allowlist
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          When empty, all IPs are allowed. Add IPs to restrict access.
        </p>

        {/* Add IP input */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            placeholder="e.g., 192.168.1.0/24"
            className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            onKeyDown={(e) => e.key === 'Enter' && handleAddIp()}
          />
          <button
            onClick={handleAddIp}
            className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* IP List */}
        {access.ip_allowlist.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm text-gray-500 dark:text-gray-400">
            No IP restrictions (all IPs allowed)
          </div>
        ) : (
          <div className="space-y-2">
            {access.ip_allowlist.map((ip) => (
              <div
                key={ip}
                className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <span className="font-mono text-sm text-gray-900 dark:text-white">{ip}</span>
                <button
                  onClick={() => handleRemoveIp(ip)}
                  className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Warning */}
      {access.ip_allowlist.length > 0 && (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            IP restrictions are active. Only requests from listed IPs will be accepted.
          </p>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={updateConfig.isPending}
        className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        {updateConfig.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Check className="w-4 h-4" />
            Save Access Settings
          </>
        )}
      </button>
    </div>
  );
}

// Main Drawer Component
export default function TenantDetailDrawer({ tenantId, isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [auditModalOpen, setAuditModalOpen] = useState(false);

  // Reset to overview tab when drawer opens with a new tenant
  useEffect(() => {
    if (isOpen) {
      setActiveTab('overview');
    }
  }, [isOpen, tenantId]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-gray-900 shadow-xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tenant Details</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{tenantId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 py-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
          {activeTab === 'overview' && <OverviewTab tenantId={tenantId} />}
          {activeTab === 'storage' && <BlobStorageTab tenantId={tenantId} />}
          {activeTab === 'llm-costs' && <LLMCostsTab tenantId={tenantId} onOpenAuditTrail={() => setAuditModalOpen(true)} />}
          {activeTab === 'features' && <FeatureFlagsTab tenantId={tenantId} />}
          {activeTab === 'limits' && <LimitsTab tenantId={tenantId} />}
          {activeTab === 'access' && <AccessTab tenantId={tenantId} />}
        </div>
      </div>

      {/* Audit Trail Modal */}
      <AuditTrailModal
        tenantId={tenantId}
        isOpen={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
      />
    </>
  );
}
